/**
 * Messages (broadcast) history page — ADR 0053. Lists the org's broadcasts
 * newest first and polls while any of them is queued/sending. Composing a new
 * one lives on /admin/messages/new (BroadcastCompose).
 */
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';

import useOrganizationStore from '@/store/organizationStore';
import useBroadcastStore, { Broadcast, BroadcastStatus } from '@/store/broadcastStore';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { MessagesSquare, Plus } from 'lucide-react';

const SCOPE_LABELS: Record<string, string> = {
  organization: 'Organization',
  registration: 'Registration',
  contest: 'Contest',
  division: 'Division',
};

export function segmentSummary(broadcast: Broadcast, orgLabel: string): string {
  const audience = broadcast.segment?.audience === 'everyone' ? 'Everyone' : 'Managers';
  const scope = broadcast.segment?.scope || 'organization';
  if (scope === 'organization') {
    return `${audience} in ${orgLabel || 'the organization'}`;
  }
  return `${audience} (${SCOPE_LABELS[scope] || scope})`;
}

function StatusBadge({ broadcast }: { broadcast: Broadcast }) {
  const status: BroadcastStatus = broadcast.status;
  if (status === 'sent') {
    return (
      <Badge variant="default">
        {typeof broadcast.sentCount === 'number' ? `${broadcast.sentCount} sent` : 'Sent'}
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive">
        {broadcast.failedCount ? `Failed (${broadcast.failedCount} failed)` : 'Failed'}
      </Badge>
    );
  }
  if (status === 'sending') return <Badge variant="outline">Sending…</Badge>;
  if (status === 'queued') return <Badge variant="outline">Queued</Badge>;
  return <Badge variant="secondary">Draft</Badge>;
}

export default function BroadcastAdmin() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as { leagueName?: string };
  // organizationStore is untyped (@ts-nocheck) — its hook returns `unknown`.
  const { organizations } = useOrganizationStore() as { organizations?: any[] };

  const { broadcasts, fetchBroadcasts: fetchBroadcastsFn } = useBroadcastStore((s) => ({
    broadcasts: s.broadcasts,
    fetchBroadcasts: s.fetchBroadcasts,
  }));
  const fetchBroadcasts = useMemo(() => fetchBroadcastsFn(getToken), [fetchBroadcastsFn, getToken]);

  const organization = organizations?.find((l: any) => l.name === leagueName);
  const orgId = organization?._id;

  useEffect(() => {
    if (orgId) fetchBroadcasts(orgId);
  }, [orgId]);

  // Poll while anything is in flight so counts/status land without a reload.
  const hasInFlight = broadcasts.some((b) => b.status === 'queued' || b.status === 'sending');
  useEffect(() => {
    if (!orgId || !hasInFlight) return;
    const interval = setInterval(() => fetchBroadcasts(orgId), 3000);
    return () => clearInterval(interval);
  }, [orgId, hasInFlight, fetchBroadcasts]);

  const orgLabel = organization?.displayName || organization?.name || leagueName;

  return (
    <PageContainer>
      <PageHeader
        title="Messages"
        description="Send one-off emails to your members: everyone in the organization, or a specific registration, contest, or division."
        actions={(
          <Button onClick={() => navigate('/admin/messages/new')} data-tour="new-message">
            <Plus className="h-4 w-4 mr-1" />
            New message
          </Button>
        )}
      />

      {broadcasts.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <MessagesSquare className="h-8 w-8 text-muted-foreground/60" />
            <div>
              <p className="font-medium">No messages yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Reach your members by email: announcements, schedule changes, reminders.
              </p>
            </div>
            <Button onClick={() => navigate('/admin/messages/new')}>
              <Plus className="h-4 w-4 mr-1" />
              Send your first message
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {broadcasts.map((b) => (
            <Card key={b._id}>
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{b.subject || '(no subject)'}</p>
                  <p className="text-sm text-muted-foreground truncate">{segmentSummary(b, orgLabel)}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <StatusBadge broadcast={b} />
                  <span className="text-sm text-muted-foreground">
                    {b.sentAt
                      ? new Date(b.sentAt).toLocaleDateString()
                      : b.created
                        ? new Date(b.created).toLocaleDateString()
                        : ''}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
